import { useCallback, useEffect, useRef, useState } from 'react';
import { captureSession, isSessionCurrent, SESSION_EVENT } from '../utils/authSession';

// Authorization changes matter even when a refreshed profile retains its login session.
export function shippingAccessScope() {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    return JSON.stringify([
      user?._id || user?.id,
      user?.role,
      user?.adminWorkspace,
      user?.clinicRole,
      user?.clinic,
      user?.clinics,
      user?.visibleReps,
      user?.isActive,
      user?.disabled,
      user?.distributor,
      user?.distributorClinicIds,
      user?.distributorSourceClientUser,
      user?.distributorPreviewExpiresAt,
    ]);
  } catch {
    return '';
  }
}

export default function useShippingRequest(recordKey) {
  const [epoch, setEpoch] = useState(0);
  const scope = shippingAccessScope();
  const active = useRef(null);
  const mounted = useRef(false);
  const key = useRef(recordKey);
  key.current = recordKey;
  const cancel = useCallback(() => {
    active.current?.controller.abort();
    active.current = null;
  }, []);
  useEffect(() => {
    mounted.current = true;
    const session = captureSession();
    const changed = () => {
      if (!isSessionCurrent(session) || scope !== shippingAccessScope()) {
        cancel();
        setEpoch((value) => value + 1);
      }
    };
    window.addEventListener('storage', changed);
    window.addEventListener(SESSION_EVENT, changed);
    return () => {
      mounted.current = false;
      cancel();
      window.removeEventListener('storage', changed);
      window.removeEventListener(SESSION_EVENT, changed);
    };
  }, [cancel, scope, recordKey, epoch]);
  const begin = useCallback(() => {
    cancel();
    const sessionBinding = captureSession();
    const access = shippingAccessScope();
    const target = key.current;
    const request = { controller: new AbortController() };
    active.current = request;
    return {
      signal: request.controller.signal,
      sessionBinding,
      isCurrent: () =>
        mounted.current &&
        active.current === request &&
        !request.controller.signal.aborted &&
        target === key.current &&
        access === shippingAccessScope() &&
        isSessionCurrent(sessionBinding),
    };
  }, [cancel]);
  return { begin, cancel, version: `${scope}:${epoch}` };
}
